import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth";
import { api, formatApiErrorDetail } from "../api";
import { ShieldCheck, Send } from "lucide-react";

export default function Login() {
  const { login, completeMfa } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState(0);  // epoch ms
  const [now, setNow] = useState(Date.now());
  // MFA state
  const [step, setStep] = useState("password"); // password | mfa | enroll
  const [preauth, setPreauth] = useState("");
  const [telegramAvailable, setTelegramAvailable] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [tgSent, setTgSent] = useState(false);

  useEffect(() => {
    if (blockedUntil <= Date.now()) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [blockedUntil]);

  useEffect(() => {
    try {
      if (sessionStorage.getItem("sh_login_notice") === "inactivity") {
        sessionStorage.removeItem("sh_login_notice");
        setError("Sessao terminada por inactividade. Inicie sessao novamente.");
      }
    } catch (e) {}
  }, []);

  const remainingSec = Math.max(0, Math.ceil((blockedUntil - now) / 1000));
  const isBlocked = remainingSec > 0;
  const mm = String(Math.floor(remainingSec / 60)).padStart(2, "0");
  const ss = String(remainingSec % 60).padStart(2, "0");

  const submit = async (e) => {
    e.preventDefault();
    if (isBlocked) return;
    setError("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res && res.mfa_required) {
        setPreauth(res.preauth_token || "");
        setTelegramAvailable(!!res.telegram_available);
        setStep(res.require_mfa_enroll ? "enroll" : "mfa");
        return;
      }
      nav("/app");
    } catch (err) {
      const status = err.response?.status;
      const retryAfter = parseInt(err.response?.headers?.["retry-after"] || "0", 10);
      if (status === 429 && retryAfter > 0) {
        setBlockedUntil(Date.now() + retryAfter * 1000);
        setError(formatApiErrorDetail(err.response.data?.detail) || "Demasiadas tentativas.");
      } else if (err.response?.data?.detail) {
        setError(formatApiErrorDetail(err.response.data.detail));
      } else if (err.response) {
        setError("Nao foi possivel iniciar sessao. Tente novamente.");
      } else {
        setError("Falha de ligacao ao servidor. Verifique a ligacao e tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await completeMfa(preauth, mfaCode);
      nav("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Codigo invalido");
    } finally { setLoading(false); }
  };

  const sendTelegram = async () => {
    setError(""); setTgSent(false);
    try {
      await api.post("/auth/mfa/telegram", { preauth_token: preauth });
      setTgSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Nao foi possivel enviar Telegram");
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 relative bg-navy">
        <img
          src="https://images.pexels.com/photos/13219418/pexels-photo-13219418.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Edificio"
          className="w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 flex flex-col justify-end p-12 bg-gradient-to-t from-[#0D1B3E] to-transparent">
          <div className="text-white text-2xl font-bold tracking-tight mb-3">SaaS-Hub</div>
          <img
            src="https://customer-assets-lxgj4vgw.emergentagent.net/job_saas-control-panel-2/artifacts/wqwak0qm_CogniSeg-with-Slogan_white.webp"
            alt="Cogniseg - Security and Compliance"
            className="w-64 max-w-full opacity-90"
            data-testid="login-cogniseg-logo"
          />
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-8">
        {step === "password" && (
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="text-blue uppercase text-xs font-semibold tracking-[0.2em] mb-2">Back office</div>
          <h1 className="text-navy text-3xl font-bold tracking-tight mb-8">Iniciar sessao</h1>
          {error && (
            <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${isBlocked ? "bg-red-50 text-red-700 border border-red-200" : "bg-red-50 text-red-600"}`} data-testid="login-error">
              {error}
              {isBlocked && (
                <div className="mt-2 flex items-center gap-2" data-testid="login-blocked">
                  <ShieldCheck size={14} />
                  <span className="font-mono font-bold">{mm}:{ss}</span>
                  <div className="flex-1 h-1 bg-red-200 rounded overflow-hidden">
                    <div className="h-full bg-red-500 transition-all" style={{ width: `${remainingSec > 0 ? Math.min(100, (remainingSec / 900) * 100) : 0}%` }} />
                  </div>
                </div>
              )}
            </div>
          )}
          <label className="block text-sm font-semibold text-navy mb-1">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email"
            placeholder="nome@empresa.com"
            autoComplete="off"
            className="w-full mb-4 px-4 py-2.5 border border-tint rounded-lg focus:outline-none focus:ring-2 focus:ring-blue"
          />
          <label className="block text-sm font-semibold text-navy mb-1">Palavra-passe</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password"
            placeholder="Introduza a palavra-passe"
            autoComplete="off"
            className="w-full mb-6 px-4 py-2.5 border border-tint rounded-lg focus:outline-none focus:ring-2 focus:ring-blue"
          />
          <button
            type="submit"
            disabled={loading || isBlocked}
            data-testid="login-submit"
            className="w-full bg-blue text-white py-2.5 rounded-lg font-semibold hover:bg-[#5BB8E8] transition-colors disabled:opacity-50"
          >
            {isBlocked ? `Bloqueado (${mm}:${ss})` : loading ? "A entrar..." : "Entrar"}
          </button>
          <div className="mt-4 text-center flex flex-col gap-1">
            <Link to="/forgot-password" data-testid="login-forgot" className="text-sm text-blue hover:text-light-blue">Esqueci-me da password</Link>
            <Link to="/portal-login" data-testid="login-portal-magic" className="text-xs text-muted2 hover:text-blue">Sou cliente e prefiro receber um link por email</Link>
          </div>
        </form>
        )}
        {step === "mfa" && (
          <form onSubmit={submitMfa} className="w-full max-w-sm" data-testid="login-mfa-form">
            <div className="text-blue uppercase text-xs font-semibold tracking-[0.2em] mb-2">Passo 2 de 2</div>
            <h1 className="text-navy text-2xl font-bold tracking-tight mb-2">Autenticacao em dois passos</h1>
            <p className="text-sm text-slate-600 mb-5">Introduza o codigo de 6 digitos da sua app autenticadora (Authy, Google Authenticator, etc), um codigo de recuperacao, ou peca um codigo via Telegram.</p>
            {error && <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-600 text-sm" data-testid="login-mfa-error">{error}</div>}
            {tgSent && <div className="mb-4 px-4 py-3 rounded-lg bg-emerald-50 text-emerald-700 text-sm" data-testid="login-tg-sent">Codigo enviado ao seu Telegram. Valido por 5 minutos.</div>}
            <label className="block text-sm font-semibold text-navy mb-1">Codigo</label>
            <input value={mfaCode} onChange={(e) => setMfaCode(e.target.value)}
              data-testid="login-mfa-code" autoComplete="one-time-code" inputMode="text"
              placeholder="6 digitos OU codigo de recuperacao"
              className="w-full mb-4 px-4 py-2.5 border border-tint rounded-lg focus:outline-none focus:ring-2 focus:ring-blue" />
            <button type="submit" disabled={loading || !mfaCode} data-testid="login-mfa-submit"
              className="w-full bg-blue text-white py-2.5 rounded-lg font-semibold hover:bg-[#5BB8E8] transition-colors disabled:opacity-50">
              {loading ? "A verificar..." : "Verificar e entrar"}
            </button>
            {telegramAvailable && (
              <>
                <button type="button" onClick={sendTelegram} data-testid="login-mfa-telegram"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-blue text-blue py-2 rounded-lg font-semibold hover:bg-blue hover:text-white transition-colors">
                  <Send size={14} /> {tgSent ? "Reenviar codigo Telegram" : "Enviar codigo por Telegram"}
                </button>
                {tgSent && (
                  <div className="mt-2 text-[12px] text-emerald-700 text-center" data-testid="login-mfa-tg-sent">
                    Codigo enviado - verifique o Telegram. Expira em 5 min.
                  </div>
                )}
              </>
            )}
            <button type="button" onClick={() => { setStep("password"); setPreauth(""); setMfaCode(""); }} data-testid="login-mfa-back"
              className="mt-4 w-full text-sm text-muted2 hover:text-navy">Voltar</button>
          </form>
        )}
        {step === "enroll" && (
          <div className="w-full max-w-md" data-testid="login-mfa-enroll">
            <div className="text-blue uppercase text-xs font-semibold tracking-[0.2em] mb-2">MFA obrigatoria</div>
            <h1 className="text-navy text-2xl font-bold tracking-tight mb-3">Precisa de activar MFA</h1>
            <div className="mb-4 px-4 py-3 rounded-lg bg-amber-50 text-amber-800 text-sm">
              O super admin forcou a autenticacao em dois passos para esta conta. Antes de continuar, tem de configurar a MFA no seu perfil.
            </div>
            <p className="text-sm text-slate-600 mb-4">Iniciamos sessao com credenciais temporarias limitadas para poder aceder ao perfil e completar o setup.</p>
            <button onClick={async () => {
              try {
                const { data } = await api.post("/auth/mfa/complete", { preauth_token: preauth, code: "__enroll__" });
                setError(""); nav("/app/perfil?tab=seguranca#mfa");
              } catch (err) {
                // Fallback: sem endpoint temp, abrimos setup via password reset link ou instrucoes
                nav("/app/perfil?tab=seguranca#mfa-forced&preauth=" + encodeURIComponent(preauth));
              }
            }} data-testid="login-enroll-continue" className="w-full bg-blue text-white py-2.5 rounded-lg font-semibold hover:bg-[#5BB8E8]">
              Continuar para setup MFA
            </button>
            <button type="button" onClick={() => setStep("password")} className="mt-3 w-full text-sm text-muted2 hover:text-navy">Voltar</button>
          </div>
        )}
      </div>
    </div>
  );
}
